<script lang="ts">
  import type { AIComment as AICommentType } from '$lib/types/grammar'

  interface Props {
    aiComment: AICommentType | null
    isLoadingAI: boolean
    isAIOnly?: boolean
    helpText?: string
    isKeptOriginal?: boolean
    onApplyAlternative?: (alternative: string) => void
    onKeepOriginal?: () => void
  }

  let {
    aiComment,
    isLoadingAI,
    isAIOnly = false,
    helpText,
    isKeptOriginal = false,
    onApplyAlternative,
    onKeepOriginal
  }: Props = $props()
</script>

<!-- AI opinion notification -->
{#if aiComment && !isAIOnly && helpText}
  {#if !aiComment.agreesWithCorrection}
    <!-- AI disagrees with correction -->
    <div class="ai-disagreement-line">
      <span class="ai-disagreement-icon">💡</span>
      <span class="ai-disagreement-text">
        {#if aiComment.suggestsKeepOriginal}
          AI는 원본 유지를 제안합니다
        {:else}
          AI는 다른 의견을 제시합니다
        {/if}
      </span>
      {#if aiComment.suggestsKeepOriginal}
        <button 
          class="keep-original-minimal keep-original-right {isKeptOriginal ? 'kept' : ''}"
          onclick={onKeepOriginal}
          title={isKeptOriginal ? "원본 유지 취소" : "원본 유지"}
        >
          {isKeptOriginal ? '원본 유지됨' : '원본 유지'}
        </button>
      {/if}
    </div>
  {:else if aiComment.alternatives && aiComment.alternatives.length > 0}
    <!-- AI agrees but has additional alternatives -->
    <div class="ai-suggestion-line">
      <span class="ai-suggestion-icon">✨</span>
      <span class="ai-suggestion-text">AI가 추가 대안을 제시합니다</span>
    </div>
  {/if}
{/if}

<!-- AI comment on separate line -->
{#if (aiComment || isLoadingAI)}
  <div class="ai-comment-line">
    <span class="ai-icon-minimal">🤖</span>
    {#if isLoadingAI}
      <span class="ai-loading-text">분석 중...</span>
    {:else if aiComment}
      <span class="ai-comment-text-minimal">
        {aiComment.text}
      </span>
    {/if}
  </div>
{/if}

<!-- AI alternatives on separate line -->
{#if aiComment?.alternatives && aiComment.alternatives.length > 0}
  <div class="ai-alternatives-line">
    <span class="alternatives-icon-space"></span>
    <div class="alternatives-content">
      <span class="alternatives-label">
        {#if aiComment.agreesWithCorrection}
          추가 대안:
        {:else}
          대안:
        {/if}
      </span>
      {#each aiComment.alternatives as alt}
        <button 
          class="ai-alternative-minimal"
          onclick={() => onApplyAlternative?.(alt)}
          title="클릭하여 적용"
        >
          {alt}
        </button>
      {/each}
    </div>
  </div>
{/if}

<style>
  /* AI disagreement line */
  .ai-disagreement-line {
    display: flex;
    align-items: flex-start;
    margin-top: 2px;
    margin-left: 2px;
    line-height: 1.4;
    font-size: 11px;
    background: rgba(239, 68, 68, 0.08);
    padding: 2px 4px;
    border-radius: 3px;
    margin-bottom: 2px;
    position: relative;
  }
  
  .ai-disagreement-icon {
    font-size: 12px;
    width: 16px;
    flex-shrink: 0;
    display: inline-block;
    text-align: center;
    margin-right: 4px;
  }
  
  .ai-disagreement-text {
    color: var(--error);
    font-size: 11px;
    flex-grow: 1;
  }
  
  /* AI suggestion line (when agrees but has alternatives) */
  .ai-suggestion-line {
    display: flex;
    align-items: flex-start;
    margin-top: 2px;
    margin-left: 2px;
    line-height: 1.4;
    font-size: 11px;
    background: rgba(59, 130, 246, 0.08);
    padding: 2px 4px;
    border-radius: 3px;
    margin-bottom: 2px;
  }
  
  .ai-suggestion-icon {
    font-size: 12px;
    width: 16px;
    flex-shrink: 0;
    display: inline-block;
    text-align: center;
    margin-right: 4px;
  }
  
  .ai-suggestion-text {
    color: var(--info);
    font-size: 11px;
    flex-grow: 1;
  }
  
  /* Position keep-original button to the right */
  .keep-original-right {
    margin-left: auto;
    margin-right: 2px;
    flex-shrink: 0;
  }
  
  /* AI comment line */
  .ai-comment-line {
    display: flex;
    align-items: flex-start;
    margin-top: 2px;
    margin-left: 2px;
    line-height: 1.4;
    font-size: 11px;
  }
  
  .ai-icon-minimal {
    font-size: 12px;
    width: 16px;
    flex-shrink: 0;
    display: inline-block;
    text-align: center;
    margin-right: 4px;
  }
  
  .ai-loading-text,
  .ai-comment-text-minimal {
    font-size: 11px;
    color: var(--text-secondary);
    line-height: 1.4;
  }
  
  .ai-loading-text {
    font-style: italic;
    color: var(--text-tertiary);
  }
  
  /* AI alternatives line */
  .ai-alternatives-line {
    display: flex;
    align-items: flex-start;
    margin-top: 2px;
    margin-left: 2px;
    line-height: 1.4;
    font-size: 11px;
  }
  
  .alternatives-icon-space {
    width: 16px;
    flex-shrink: 0;
    margin-right: 4px;
  }
  
  .alternatives-content {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }
  
  .alternatives-label {
    font-size: 11px;
    color: var(--text-secondary);
    font-weight: 500;
    margin-right: 4px;
  }
  
  .ai-alternative-minimal,
  .keep-original-minimal {
    display: inline-block;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 10px;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.15s ease;
    margin-left: 4px;
  }
  
  .ai-alternative-minimal:hover,
  .keep-original-minimal:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
  }
  
  .keep-original-minimal {
    background: rgba(245, 158, 11, 0.08);
    border-color: rgba(245, 158, 11, 0.2);
    color: var(--warning);
  }
  
  .keep-original-minimal:hover {
    background: rgba(245, 158, 11, 0.15);
    border-color: rgba(245, 158, 11, 0.3);
  }
  
  .keep-original-minimal.kept {
    background: rgba(245, 158, 11, 0.25);
    border-color: rgba(245, 158, 11, 0.4);
    color: var(--warning);
    font-weight: 500;
  }
  
  .keep-original-minimal.kept:hover {
    background: rgba(245, 158, 11, 0.2);
    border-color: rgba(245, 158, 11, 0.35);
  }
  
  
  /* Dark mode support */
  @media (prefers-color-scheme: dark) {
    .ai-disagreement-line {
      background: rgba(239, 68, 68, 0.1);
    }
    
    .keep-original-minimal {
      background: rgba(245, 158, 11, 0.1);
      border-color: rgba(245, 158, 11, 0.25);
    }
    
    .keep-original-minimal:hover {
      background: rgba(245, 158, 11, 0.2);
      border-color: rgba(245, 158, 11, 0.35);
    }
    
    .keep-original-minimal.kept {
      background: rgba(245, 158, 11, 0.3);
      border-color: rgba(245, 158, 11, 0.45);
    }
    
    .keep-original-minimal.kept:hover {
      background: rgba(245, 158, 11, 0.25);
      border-color: rgba(245, 158, 11, 0.4);
    }
  }
  
  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    .ai-alternative-minimal,
    .keep-original-minimal {
      transition: none;
    }
  }
</style>